<template>
  <!-- Grey placeholder (same look as content-data.vue's image-error fallback) replaces the player once
       the composable reports an unplayable video or a failed API script load. -->
  <div v-if="hasError" class="aspect-[1.8] w-full h-full bg-gray-30 rounded"></div>

  <!-- placeholder <div> — the YT IFrame Player API creates & owns its own <iframe> here
       (see useYoutubeResumablePlayer.ts for why: onReady never fires reliably when attaching to a
       pre-existing iframe). Outer wrapper is the stable, Vue-owned node used for scroll-pause observation
       and aspect-ratio sizing; the inner div is consumed/replaced by the Player API. -->
  <div v-else ref="youtubeWrapperRef" class="relative w-full aspect-video bg-gray-30 rounded">
    <div ref="youtubePlaceholderRef"></div>
  </div>
</template>

<script setup lang="ts">
interface Props {
  /** YouTube videoId (type=3 node's `meta` value in the parent content tree) */
  videoId: string
}

defineOptions({ name: 'ContentYoutube' })

const props = defineProps<Props>()

const youtubeWrapperRef = useTemplateRef<HTMLElement>('youtubeWrapperRef')
const youtubePlaceholderRef = useTemplateRef<HTMLElement>('youtubePlaceholderRef')

const videoId = computed(() => props.videoId)

// 影片播放時間點記憶（跨 preview/modal 實例共用同一 videoId 的續播位置，Figma 3657:7933）
const { pause: pauseYoutube, hasError } = useYoutubeResumablePlayer(youtubePlaceholderRef, videoId)

// 影片捲出視窗時暫停播放。呼叫 composable 暴露的 pause()（直接操作真實 Player 物件），
// 不用 postMessage/instanceof 檢查 iframe——YT Player API 會把 youtubeWrapperRef 底下的
// placeholder <div> 換成它自己建立的 <iframe>，Vue template ref 不會自動跟著換。
const { stop: stopObserveYoutube } = useIntersectionObserver(
  youtubeWrapperRef,
  entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) pauseYoutube?.()
    })
  },
  {
    rootMargin: '0px',
    threshold: [0],
  }
)

onUnmounted(() => {
  stopObserveYoutube()
})
</script>
